import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form, Button, Card, Badge, Modal } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import PageLayout from "../../layouts/PageLayout";

export default function ApprovedProductsListPage() {
  const { t } = useContext(TranslatorContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockKeepers, setStockKeepers] = useState([]);
  const [totalApprovedCount, setTotalApprovedCount] = useState(0);

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [stockKeeperId, setStockKeeperId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeImage, setActiveImage] = useState("");

  const formatText = (text) => {
    if (!text) return "";
    let clean = text;
    
    clean = clean
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");

    if (typeof DOMParser !== "undefined") {
      const doc = new DOMParser().parseFromString(clean, "text/html");
      clean = doc.body.textContent || "";
    } else {
      clean = clean.replace(/<[^>]*>/g, "");
    }

    return clean.trim();
  };

  const tableHeaders = [
    { name: "PRODUCT CODE", width: "130px" },
    { name: "IMAGE", width: "90px" },
    { name: "TITLE", width: "220px" },
    { name: "CATEGORY", width: "140px" },
    { name: "BRAND", width: "120px" },
    { name: "SELLING STYLE", width: "120px" },
    { name: "PRICE ($)", width: "120px" },
    { name: "QTY", width: "70px" },
    { name: "ACTION", width: "120px" }
  ];

  const fetchInitialData = useCallback(async () => {
    try {
      const [catRes, keeperRes] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_URL}/checker/products/initial-data`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/checker/stock-keeper`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        })
      ]);

      if (catRes.status === "fulfilled" && catRes.value.data?.success) {
        setCategories(catRes.value.data?.data?.categories || catRes.value.data?.data?.product_categories || []);
      }
      if (keeperRes.status === "fulfilled" && keeperRes.value.data?.success) {
        setStockKeepers(keeperRes.value.data?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchApprovedProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (search) params.search = search;
      if (categoryId) params.category_id = categoryId;
      if (stockKeeperId) params.stock_keeper_id = stockKeeperId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/checker/checked-products`,
        {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      const resData = response.data;

      if (resData?.data) {
        setProducts(resData.data);
        setTotalApprovedCount(resData.total_checked_products ?? resData.data.length);
      } else {
        setProducts([]);
        setTotalApprovedCount(0);
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "Failed to fetch approved products. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [search, categoryId, stockKeeperId, fromDate, toDate]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchApprovedProducts();
  }, [fetchApprovedProducts]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchApprovedProducts();
  };

  const handleResetFilters = () => {
    setSearch("");
    setCategoryId("");
    setStockKeeperId("");
    setFromDate("");
    setToDate("");
  };

  const handleOpenDetail = (product) => {
    setSelectedProduct(product);
    const mainImg =
      product.first_img ||
      product.card_img ||
      (product.images && product.images[0]?.image) ||
      "";
    setActiveImage(mainImg);
    setShowDetailModal(true);
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card mb-4">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                Approved Products ({totalApprovedCount})
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home") || "Home"}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products") || "Products"}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">Approved Products</li>
              </ul>
            </div>
          </div>
        </Col>

        <Col xl={12} className="mb-4">
          <Row className="g-3">
            <Col sm={6} lg={4}>
              <FloatCardComponent
                variant="lg green"
                digit={totalApprovedCount}
                title="Total Approved Products"
                icon="verified"
              />
            </Col>
          </Row>
        </Col>

        <Col xl={12}>
          <div className="mc-card mb-4 p-3 p-md-4">
            <Form onSubmit={handleFilterSubmit}>
              <Row className="g-3 align-items-end">
                <Col xl={3} lg={4} md={6} sm={6} xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>
                      SEARCH
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Title or Code..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>
                      CATEGORY
                    </Form.Label>
                    <Form.Select
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>
                      STOCK KEEPER
                    </Form.Label>
                    <Form.Select
                      value={stockKeeperId}
                      onChange={(e) => setStockKeeperId(e.target.value)}
                    >
                      <option value="">All Keepers</option>
                      {stockKeepers.map((keeper) => (
                        <option key={keeper.id} value={keeper.id}>
                          {keeper.name || keeper.title || `Keeper #${keeper.id}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>
                      FROM DATE
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-uppercase text-secondary" style={{ fontSize: "11px" }}>
                      TO DATE
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xl={1} lg={4} md={12} sm={12} xs={12}>
                  <div className="d-flex gap-2 w-100">
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-100 text-uppercase fw-semibold px-2"
                      style={{ backgroundColor: "#2b77c0", borderColor: "#2b77c0", height: "38px" }}
                    >
                      FILTER
                    </Button>
                    <Button
                      type="button"
                      variant="light"
                      className="w-100 text-uppercase fw-semibold border px-2"
                      onClick={handleResetFilters}
                      style={{ height: "38px" }}
                    >
                      RESET
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card p-0" style={{ overflow: "hidden" }}>
            {error && (
              <div className="p-3">
                <Alert variant="danger" className="m-0">
                  {error}
                </Alert>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading approved products...</p>
              </div>
            ) : (
              <div className="w-100" style={{ overflowX: "auto" }}>
                <table
                  className="table align-middle text-center table-hover mb-0"
                  style={{ width: "100%", minWidth: "1050px", tableLayout: "fixed" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#2b77c0", color: "#ffffff" }}>
                      {tableHeaders.map((header, index) => (
                        <th
                          key={index}
                          style={{
                            width: header.width,
                            padding: "12px 8px",
                            fontSize: "12px",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            color: "#ffffff",
                            border: "none"
                          }}
                        >
                          {header.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.length > 0 ? (
                      products.map((item) => (
                        <tr key={item.id} style={{ borderBottom: "1px solid #e9ecef" }}>
                          <td style={{ padding: "12px 8px" }}>
                            <span className="fw-bold text-primary text-truncate d-block">
                              {item.product_code}
                            </span>
                          </td>
                          <td style={{ padding: "12px 8px" }}>
                            <div
                              className="d-flex justify-content-center align-items-center rounded bg-light"
                              style={{ width: "45px", height: "45px", margin: "0 auto", overflow: "hidden" }}
                            >
                              <img
                                src={
                                  item.first_img
                                    ? `${process.env.REACT_APP_IMG_URL}${item.first_img}`
                                    : item.card_img
                                      ? `${process.env.REACT_APP_IMG_URL}${item.card_img}`
                                      : "/assets/images/placeholder.png"
                                }
                                alt={item.title}
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/45?text=No+Img";
                                }}
                              />
                            </div>
                          </td>
                          <td className="text-start" style={{ padding: "12px 8px" }}>
                            <div
                              className="text-truncate"
                              style={{ maxWidth: "100%" }}
                              title={item.title}
                            >
                              {item.title}
                            </div>
                          </td>
                          <td className="text-truncate" style={{ padding: "12px 8px" }}>
                            {item.category?.name || "N/A"}
                          </td>
                          <td className="text-truncate" style={{ padding: "12px 8px" }}>
                            {item.brand?.name || "N/A"}
                          </td>
                          <td style={{ padding: "12px 8px" }}>
                            <Badge bg={item.selling_style === "Fixed" ? "info" : "warning"}>
                              {item.selling_style}
                            </Badge>
                          </td>
                          <td className="fw-bold text-dark" style={{ padding: "12px 8px" }}>
                            ${item.sell_price_dollar ? Number(item.sell_price_dollar).toLocaleString() : "0"}
                          </td>
                          <td style={{ padding: "12px 8px" }}>{item.product_qty}</td>
                          <td style={{ padding: "12px 8px" }}>
                            <Button
                              variant="primary"
                              size="sm"
                              className="px-2 py-1 text-nowrap"
                              onClick={() => handleOpenDetail(item)}
                              style={{ fontSize: "12px" }}
                            >
                              VIEW DETAIL
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={tableHeaders.length} className="text-center py-4 text-muted">
                          No approved products found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {selectedProduct && (
        <Modal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton className="px-4 py-3 border-bottom">
            <Modal.Title className="fs-5 fw-bold text-dark">
              Product Detail ({selectedProduct.product_code})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Row className="g-4">
              <Col md={5} className="text-center">
                <div
                  className="border rounded p-3 mb-3 bg-white d-flex align-items-center justify-content-center"
                  style={{ minHeight: "280px" }}
                >
                  <img
                    src={
                      activeImage
                        ? `${process.env.REACT_APP_IMG_URL}${activeImage}`
                        : "/assets/images/placeholder.png"
                    }
                    alt={selectedProduct.title}
                    className="img-fluid rounded"
                    style={{ maxHeight: "300px", objectFit: "contain" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300?text=No+Image";
                    }}
                  />
                </div>

                {selectedProduct.images && selectedProduct.images.length > 0 && (
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    {selectedProduct.images.map((imgObj) => (
                      <div
                        key={imgObj.id}
                        onClick={() => setActiveImage(imgObj.image)}
                        className={`border rounded p-1 ${activeImage === imgObj.image ? "border-primary border-2" : ""}`}
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          src={`${process.env.REACT_APP_IMG_URL}${imgObj.image}`}
                          alt="Gallery"
                          style={{ width: "50px", height: "50px", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/50?text=Img";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Col>

              <Col md={7}>
                <h4 className="mb-2 fw-bold text-dark">{selectedProduct.title}</h4>
                <p className="text-muted mb-3">
                  Product Code: <span className="fw-semibold text-primary">{selectedProduct.product_code}</span>
                </p>

                <Card className="p-3 mb-3 bg-light border-0">
                  <Row className="g-3">
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Category
                      </small>
                      <span className="fs-6 fw-bold text-dark">{selectedProduct.category?.name || "N/A"}</span>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Brand
                      </small>
                      <span className="fs-6 fw-bold text-dark">{selectedProduct.brand?.name || "N/A"}</span>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Product Condition
                      </small>
                      <Badge bg="secondary" className="px-2 py-1 text-capitalize">
                        {selectedProduct.product_condition}
                      </Badge>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Selling Style
                      </small>
                      <Badge
                        bg={selectedProduct.selling_style === "Fixed" ? "info" : "warning"}
                        className="px-2 py-1"
                      >
                        {selectedProduct.selling_style}
                      </Badge>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Buying Price Dollar
                      </small>
                      <span className="fs-5 fw-bold text-success">
                        ${Number(selectedProduct.sell_price_dollar || 0).toLocaleString()}
                      </span>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Buying Price
                      </small>
                      <span className="fs-6 fw-bold text-dark">
                        {Number(selectedProduct.buying_price || 0).toLocaleString()}
                      </span>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Shipping Charges Dollar
                      </small>
                      <span className="fs-6 fw-bold text-dark">
                        ${Number(selectedProduct.shipping_charges_dollar || 0).toLocaleString()}
                      </span>
                    </Col>
                    <Col sm={6}>
                      <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                        Product Quantity
                      </small>
                      <span className="fs-6 fw-bold text-dark">{selectedProduct.product_qty}</span>
                    </Col>
                  </Row>
                </Card>

                {selectedProduct.short_description && (
                  <div className="mb-3">
                    <h6 className="fw-bold text-secondary mb-1">Short Description</h6>
                    <div className="text-muted small">
                      {formatText(selectedProduct.short_description)}
                    </div>
                  </div>
                )}
              </Col>

              {selectedProduct.description && (
                <Col md={12}>
                  <hr className="my-2" />
                  <h6 className="fw-bold text-secondary mb-2">Description</h6>
                  <div className="border rounded p-3 bg-light text-muted small">
                    {formatText(selectedProduct.description)}
                  </div>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer className="px-4 py-2 border-top">
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </PageLayout>
  );
}